import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Profession } from '../../models/profession';
import { Race } from '../../models/race';
import { equipmentTrainerData } from './equipmentTrainerData';
import { AppThunk } from '../../app/store';
import { Equipment } from '../../models/equipment';
import { calcTrainerResults, getEqInSlot } from './equipmentTrainerCalc';

const data = {
  races: equipmentTrainerData.getRaces(),
  professions: equipmentTrainerData.getProfessions(),
  toggleOptions: equipmentTrainerData.getToggleOptions()
};

const posturesEnabled = (rank: number, raceId: string): boolean => {
  const result = rank >= 7 || ['fade', 'dreadlord', 'grayman'].includes(raceId);
  return result;
}

const calcState = (state: EquipmentTrainerState) => {
  calcProfessions(state);
  calcMounted(state);
  calcStats(state);
  calcWeapon(state);
  calcMood(state);
  calcPosture(state);
  calcTrainerResults(state);
  return state;
}

const calcProfessions = (state: EquipmentTrainerState) => {
  const filtered = state.professions.filter(x => !state.raceId || x.raceIds.includes(state.raceId));
  let valid = filtered.some(x => x.id === state.professionId);  
  if (!valid) {
    state.professionId = filtered[0].id ?? '';
  }
  state.professionOptions = filtered;
}


const calcStats = (state: EquipmentTrainerState) => {
  if (!state.statsDirty) {
    switch (state.professionId) {
      case 'warrior':
        if (state.raceId !== 'trolloc') {
          [state.strn, state.intl, state.wilp, state.dext, state.cont] = [19, 12, 12, 18, 19];
        } else {
          [state.strn, state.intl, state.wilp, state.dext, state.cont] = [19, 7, 7, 17, 19];
        }
        break;
      case 'rogue':
        if (state.raceId !== 'trolloc') {
          [state.strn, state.intl, state.wilp, state.dext, state.cont] = [18, 12, 12, 19, 19];
        } else {
          [state.strn, state.intl, state.wilp, state.dext, state.cont] = [17, 7, 7, 19, 19];
        }
        break;
      case 'hunter':
        if (state.raceId !== 'trolloc') {
          [state.strn, state.intl, state.wilp, state.dext, state.cont] = [18, 12, 12, 19, 19];
        } else {
          [state.strn, state.intl, state.wilp, state.dext, state.cont] = [19, 7, 7, 17, 19];
        }
        break;
      case 'fchanneler':
        [state.strn, state.intl, state.wilp, state.dext, state.cont] = [14, 18, 18, 18, 14];
        break;
      case 'mchanneler':
        [state.strn, state.intl, state.wilp, state.dext, state.cont] = [16, 18, 18, 18, 14];
        break;
      case 'fade':
        [state.strn, state.intl, state.wilp, state.dext, state.cont] = [19, 12, 12, 18, 19];
        break;
    }
  }
}

const calcMounted = (state: EquipmentTrainerState) => {
  if (state.raceId !== 'trolloc') {
    state.mountedDisabled = false;
    state.mounted = state.lastMounted ?? true;
    state.ride = state.lastRide ?? 4;
    state.charge = state.lastCharge ?? 0;
  } else {
    state.mountedDisabled = true;
    state.mounted = false;
    state.ride = 0;
    state.charge = 0;
  }
}

const calcMood = (state: EquipmentTrainerState) => {
  if (state.lastMood !== null) {
    state.mood = state.lastMood;
  } else {
    switch (state.professionId) {
      case 'warrior':
        state.mood = 'mood-brave';
        break;
      case 'rogue':
        state.mood = 'mood-wimpy';
        break;
      case 'hunter':
        state.mood = 'mood-brave';
        break;
      case 'fchanneler':
      case 'mchanneler':
        state.mood = 'mood-wimpy';
        break;
      case 'fade':
        state.mood = 'mood-brave';
        break;
    }
  }
}

const calcPosture = (state: EquipmentTrainerState) => {
  if (posturesEnabled(state.rank, state.raceId)) {
    state.posturesDisabled = false;
    if (state.lastPosture !== null) {
      state.posture = state.lastPosture;
    } else {
      switch (state.professionId) {
        case 'warrior':
          state.posture = 'posture-offensive';
          break;
        case 'rogue':
          state.posture = 'posture-defensive';
          break;
        case 'hunter':
          state.posture = 'posture-offensive';
          break;
        case 'fchanneler':
        case 'mchanneler':
          state.posture = 'posture-defensive';
          break;
        case 'fade':
          state.posture = 'posture-offensive';
          break;
      }
    }
  } else {
    state.posturesDisabled = true;
    state.posture = 'posture-none';
  }
}

const calcWeapon = (state: EquipmentTrainerState) => {
  const wielded = getEqInSlot(state, 'wielded');
  if (wielded) {
    const maxWeight = state.strn < 17 ? state.strn : state.strn + 1;
    const valid = wielded.weight <= maxWeight &&
      (!state.weaponType || wielded.weaponType === state.weaponType) &&
      (!state.weaponHands || wielded.weaponHands === Math.floor(state.weaponHands));
    if (!valid) {
      state.worn.wielded = '';
    }
  }
}

const initialState: EquipmentTrainerState = calcState({
  strn: 19,
  intl: 12,
  wilp: 12,
  dext: 18,
  cont: 19,
  statsDirty: false,
  races: data.races,
  raceOptions: data.races,
  raceId: 'human',
  professions: data.professions,
  professionOptions: data.professions,
  professionId: 'warrior',
  level: 51,
  rank: 0,
  weapon: 99,
  dodge: 99,
  shield: 99,
  ride: 0,
  lastRide: null,
  weight: 0,
  mounted: false,
  lastMounted: null,
  mountedDisabled: false,
  position: 'position-standing',
  mood: 'mood-wimpy',
  lastMood: null,
  posture: 'posture-none',
  lastPosture: null,
  posturesDisabled: true,
  weaponGrade: 'weapongrade-hardened',
  armorWeave: false,
  chillWeave: false,
  blindWeave: false,
  contagionWeave: false,
  darknessWeave: false,
  weaving: false,
  bonded: false,
  equipment: [],
  groupWeapons: false,
  weaponType: '',
  weaponHands: 0,
  sortWeapons: '',
  sortArmor: '',
  bash: 99,
  kick: 99,
  stab: 0,
  hide: 0,
  sneak: 0,
  notice: 89,
  charge: 0,
  attack: 0,
  lastCharge: null,
  terrain: 'terrain-road',
  opponentLevel: 30,
  opponentOb: 196,
  opponentDb: 1,
  opponentPb: 24,
  opponentNotice: 89,
  opponentWeaponWeight: 17.3,
  opponentClubs: false,
  opponentSat: false,
  ob: 0,
  db: 0,
  pb: 0,
  abs: 0,
  mvs: 0,
  damage: [{}],
  meanDamage: 0,
  stdDevDamage: 0,
  totalWeight: 0,
  weaponWeight: 0,
  hitChance: 0,
  opponentHitChance: 0,
  bashChance: 0,
  opponentBashChance: 0,
  kickChance: 0,
  opponentKickChance: 0,
  chargeChance: 0,
  opponentChargeChance: 0,
  stabChance: 0,
  opponentStabChance: 0,
  attackChance: 0,
  flying: false,
  worn: {
    finger1: '',
    finger2: '',
    head: '',
    neck1: '',
    neck2: '',
    body: '',
    cloak: '',
    sheath: '',
    arms: '',
    wrist1: '',
    wrist2: '',
    hands: '',
    shield: '',
    wielded: '',
    waist: '',
    legs: '',
    feet: ''
  }
});

export interface EquipmentTrainerState {
  strn: number;
  intl: number;
  wilp: number;
  dext: number;
  cont: number;
  statsDirty: boolean;
  races: Race[];
  raceOptions: Race[];
  raceId: string;
  professions: Profession[];
  professionOptions: Profession[];
  professionId: string;
  level: number;
  rank: number;
  weapon: number;
  dodge: number;
  shield: number;
  ride: number;
  lastRide: number | null;
  weight: number;
  mounted: boolean;
  lastMounted: boolean | null;
  mountedDisabled: boolean;
  position: string;
  mood: string;
  lastMood: string | null;
  posture: string;
  lastPosture: string | null;
  posturesDisabled: boolean;
  weaponGrade: string;
  armorWeave: boolean;
  chillWeave: boolean;
  blindWeave: boolean;
  contagionWeave: boolean;
  darknessWeave: boolean;
  weaving: boolean;
  bonded: boolean;
  equipment: Equipment[];
  groupWeapons: boolean;
  weaponType: string;
  weaponHands: number;
  sortWeapons: string;
  sortArmor: string;
  bash: number;
  kick: number;
  stab: number;
  hide: number;
  sneak: number;
  notice: number;
  charge: number;
  attack: number;
  lastCharge: number | null;
  terrain: string;
  opponentLevel: number;
  opponentOb: number;
  opponentDb: number;
  opponentPb: number;
  opponentNotice: number;
  opponentWeaponWeight: number;
  opponentClubs: boolean;
  opponentSat: boolean;
  ob: number;
  db: number;
  pb: number;
  abs: number;
  mvs: number;
  damage: any[];
  meanDamage: number;
  stdDevDamage: number;
  totalWeight: number;
  weaponWeight: number;
  hitChance: number;
  opponentHitChance: number;
  bashChance: number;
  opponentBashChance: number;
  kickChance: number;
  opponentKickChance: number;
  chargeChance: number;
  opponentChargeChance: number;
  stabChance: number;
  opponentStabChance: number;
  attackChance: number;
  flying: boolean;
  worn: Worn;
}

interface Worn {
  finger1: string;
  finger2: string;
  head: string;
  neck1: string;
  neck2: string;
  body: string;
  cloak: string;
  sheath: string;
  arms: string;
  wrist1: string;
  wrist2: string;
  hands: string;
  shield: string;
  wielded: string;
  waist: string;
  legs: string;
  feet: string;
}

const slice = createSlice({
  name: 'equipmentTrainer',
  initialState,
  reducers: {
    removeAll: (state) => {
      state.worn = {
        finger1: '',
        finger2: '',
        head: '',
        neck1: '',
        neck2: '',
        body: '',
        cloak: '',
        sheath: '',
        arms: '',
        wrist1: '',
        wrist2: '',
        hands: '',
        shield: '',
        wielded: '',
        waist: '',
        legs: '',
        feet: ''
      };
    },

    setGroupWeapons: (state, action: PayloadAction<boolean>) => {
      state.groupWeapons = action.payload;
    },
    
    setWeaponType: (state, action: PayloadAction<string>) => {
      state.weaponType = action.payload;
      calcWeapon(state);
    },

    setWeaponHands: (state, action: PayloadAction<number>) => {
      state.weaponHands = action.payload;
      calcWeapon(state);
    },
    
    setSortWeapons: (state, action: PayloadAction<string>) => {
      state.sortWeapons = action.payload;
    },

    setSortArmor: (state, action: PayloadAction<string>) => {
      state.sortArmor = action.payload;
    },

    setStrn: (state, action: PayloadAction<number>) => {
      state.strn = action.payload;
      state.statsDirty = true;
      calcWeapon(state);
    },

    setIntl: (state, action: PayloadAction<number>) => {
      state.intl = action.payload;
      state.statsDirty = true;
    },

    setWilp: (state, action: PayloadAction<number>) => {
      state.wilp = action.payload;
      state.statsDirty = true;
    },

    setDext: (state, action: PayloadAction<number>) => {
      state.dext = action.payload;
      state.statsDirty = true;
    },

    setCont: (state, action: PayloadAction<number>) => {
      state.cont = action.payload;
      state.statsDirty = true;
    },
    
    setRaceId: (state, action: PayloadAction<string>) => {
      state.raceId = action.payload;
      calcProfessions(state);
      calcMounted(state);
      calcStats(state);
      calcWeapon(state);
      calcMood(state);
      calcPosture(state);
    },

    setProfessionId: (state, action: PayloadAction<string>) => {
      state.professionId = action.payload;
      calcStats(state);
      calcWeapon(state);
      calcMood(state);
      calcPosture(state);
    },

    setLevel: (state, action: PayloadAction<number>) => {
      state.level = action.payload;
    },

    setRank: (state, action: PayloadAction<number>) => {
      state.rank = action.payload;
      calcPosture(state);
    },

    setWeapon: (state, action: PayloadAction<number>) => {
      state.weapon = action.payload;
    },

    setDodge: (state, action: PayloadAction<number>) => {
      state.dodge = action.payload;
    },

    setShield: (state, action: PayloadAction<number>) => {
      state.shield = action.payload;
    },

    setRide: (state, action: PayloadAction<number>) => {
      state.ride = action.payload;
      state.lastRide = action.payload;
    },

    setWeight: (state, action: PayloadAction<number>) => {
      state.weight = action.payload;
    },

    setSelectedToggles: (state, action: PayloadAction<any>) => {
      for (let key of Object.keys(action.payload)) {
        const value = action.payload[key];
        (state as any)[key] = value;
        if (key === 'mounted') {
          state.lastMounted = value;
        } else if (key === 'mood') {
          state.lastMood = value;
        } else if (key === 'posture' && value !== 'posture-none') {
          state.lastPosture = value;
        } else if (key === 'terrain') {
          state.terrain = value;
        }
      }
    },

    setBash: (state, action: PayloadAction<number>) => {
      state.bash = action.payload;
    },

    setKick: (state, action: PayloadAction<number>) => {
      state.kick = action.payload;
    },

    setStab: (state, action: PayloadAction<number>) => {
      state.stab = action.payload;
    },

    setHide: (state, action: PayloadAction<number>) => {
      state.hide = action.payload;
    },

    setSneak: (state, action: PayloadAction<number>) => {
      state.sneak = action.payload;
    },

    setNotice: (state, action: PayloadAction<number>) => {
      state.notice = action.payload;
    },

    setCharge: (state, action: PayloadAction<number>) => {
      state.charge = action.payload;
      state.lastCharge = action.payload;
    },

    setAttack: (state, action: PayloadAction<number>) => {
      state.attack = action.payload;
    },

    setTerrain: (state, action: PayloadAction<string>) => {
      state.terrain = action.payload;
    },

    setOpponentLevel: (state, action: PayloadAction<number>) => {
      state.opponentLevel = action.payload;
    },

    setOpponentOb: (state, action: PayloadAction<number>) => {
      state.opponentOb = action.payload;
    },

    setOpponentDb: (state, action: PayloadAction<number>) => {
      state.opponentDb = action.payload;
    },

    setOpponentPb: (state, action: PayloadAction<number>) => {
      state.opponentPb = action.payload;
    },

    setOpponentNotice: (state, action: PayloadAction<number>) => {
      state.opponentNotice = action.payload;
    },

    setOpponentWeaponWeight: (state, action: PayloadAction<number>) => {
      state.opponentWeaponWeight = action.payload;
    },

    setOpponentClubs: (state, action: PayloadAction<boolean>) => {
      state.opponentClubs = action.payload;
    },

    setOpponentSat: (state, action: PayloadAction<boolean>) => {
      state.opponentSat = action.payload;
    },

    setWornItem(state, action: PayloadAction<{key: string, value: string}>) {
      (state.worn as any)[action.payload.key] = action.payload.value;
    },

    applyOpponentBuild: (state, action: PayloadAction<string>) => {
      switch (action.payload) {
        case 'abs_warrior_common': //claymore, non-master, 6 ride, 2 lb
          state.opponentOb = 196;
          state.opponentDb = 1;
          state.opponentPb = 24;
          state.opponentWeaponWeight = 17.3;
          state.opponentClubs = false;
          state.opponentSat = false;
          break;
        case 'abs_warrior_elite': // heron gsword, master, 7 ride, 2 lb
          state.opponentOb = 207;
          state.opponentDb = 51;
          state.opponentPb = 43;
          state.opponentWeaponWeight = 17;
          state.opponentClubs = false;
          state.opponentSat = false;
          break;
        case 'combo_hunter_common': //i&g, non-master, 6 ride, 2 lb
          state.opponentOb = 171;
          state.opponentDb = 79;
          state.opponentPb = 153;
          state.opponentWeaponWeight = 14.5;
          state.opponentClubs = false;
          state.opponentSat = false;
          break;
        case 'combo_hunter_elite': //razor, master, 7 ride, 2 lb
          state.opponentOb = 183;
          state.opponentDb = 78;
          state.opponentPb = 150;
          state.opponentWeaponWeight = 15.1;
          state.opponentClubs = false;
          state.opponentSat = false;
          break;
        case 'dodge_rogue_common': //red stone, non-master, dismounted, 2 lb
          state.opponentOb = 150;
          state.opponentDb = 145;
          state.opponentPb = 159;
          state.opponentWeaponWeight = 1.5;
          state.opponentClubs = false;
          state.opponentSat = false;
          break;
        case 'dodge_rogue_elite': //sai, master, dismounted, 2 lb
          state.opponentOb = 163;
          state.opponentDb = 153;
          state.opponentPb = 179;
          state.opponentWeaponWeight = 1.5;
          state.opponentClubs = false;
          state.opponentSat = false;
          break;
        case 'dodge_channeler_common': //yew, non-master, dismounted, 1.5 lb
          state.opponentOb = 127;
          state.opponentDb = 151;
          state.opponentPb = 186;
          state.opponentWeaponWeight = 3;
          state.opponentClubs = false;
          state.opponentSat = false;
          break;
        case 'dodge_channeler_elite': //yew, master, dismounted, 1.5 lb
          state.opponentOb = 127;
          state.opponentDb = 162;
          state.opponentPb = 209;
          state.opponentWeaponWeight = 3;
          state.opponentClubs = false;
          state.opponentSat = false;
          break;
      }
    },

    applyArmorSet: (state, action: PayloadAction<string>) => {
      switch (action.payload) {
        case 'abs_standard':
          state.worn.head = 'a full metal helmet and visor';
          state.worn.body = 'a shining steel breastplate';
          state.worn.arms = 'a pair of ebony-steel plate vambraces';
          state.worn.hands = 'a pair of heavy metal gauntlets';
          state.worn.legs = 'a pair of ebony-steel plate greaves';
          state.worn.feet = 'a pair of thick metal boots';
          break;
        case 'abs_murgoz':
          state.worn.head = 'a polished, slitted great helm';
          state.worn.body = 'a thick, steel-plated breastplate';
          state.worn.arms = 'a pair of steel-plated vambraces';
          state.worn.hands = 'a pair of steel-plated gauntlets';
          state.worn.legs = 'a pair of steel-plated greaves';
          state.worn.feet = 'a dark pair of steel boots';
          break;
        case 'combo_standard':
          state.worn.head = 'a rimmed round helmet';
          state.worn.body = 'a tunic of finely-crafted chain';
          state.worn.arms = 'a pair of riveted chainmail sleeves';
          state.worn.hands = 'a sturdy pair of full leather gauntlets';
          state.worn.legs = 'a set of riveted chainmail leggings';
          state.worn.feet = 'a pair of lissome leather boots with metal capped toes';
          break;
        case 'combo_gold':
          state.worn.head = 'a gold-chased morion with an engraved silver comb';
          state.worn.body = 'an engraved gold-plated breastplate';
          state.worn.arms = 'a pair of polished, gold-plated vambraces';
          state.worn.hands = 'a sturdy pair of full leather gauntlets';
          state.worn.legs = 'a pair of thick, gold-plated greaves';
          state.worn.feet = 'a pair of gleaming metal boots';
          break;
        case 'combo_rare':
          state.worn.head = 'a gold-chased morion with an engraved silver comb';
          state.worn.body = 'an ornate chainmail shirt chased with gold';
          state.worn.arms = 'a pair of polished, gold-plated vambraces';
          state.worn.hands = 'a sturdy pair of full leather gauntlets';
          state.worn.legs = 'a pair of thick, gold-plated greaves';
          state.worn.feet = 'a pair of gleaming metal boots';
          break;
        case 'dodge_standard':
          state.worn.head = 'a camouflaged hood';
          state.worn.body = 'a bearskin tunic';
          state.worn.arms = 'a set of cloth sleeves';
          state.worn.hands = 'a pair of dark gloves';
          state.worn.legs = 'a pair of earthen colored breeches';
          state.worn.feet = 'a black pair of silver-tooled boots';
          break;
        case 'dodge_rare':
          state.worn.head = 'a camouflaged hood';
          state.worn.body = 'a black shirt trimmed with lace';
          state.worn.arms = 'a set of cloth sleeves';
          state.worn.hands = 'a pair of dark gloves';
          state.worn.legs = 'a pair of earthen colored breeches';
          state.worn.feet = 'a black pair of silver-tooled boots';
          break;
        }
    },

    applyTrinketSet: (state, action: PayloadAction<string>) => {
      switch (action.payload) {
        case 'abs_standard':
          state.worn.finger1 = state.worn.finger2 = 'a gold ring delicately carved with ivy';
          state.worn.neck1 = state.worn.neck2 = 'a torc of gleaming steel';
          state.worn.cloak = 'an earthen brown mantle';
          state.worn.wrist1 = state.worn.wrist2 = 'a sungwood bracelet';
          state.worn.waist = 'a bronze belt of odd design';
          state.worn.sheath = '';
          break;
        case 'abs_moves':
          state.worn.finger1 = state.worn.finger2 = 'a ring of silver';
          state.worn.neck1 = state.worn.neck2 = 'a torc of gleaming steel';
          state.worn.cloak = 'an earthen brown mantle';
          state.worn.wrist1 = state.worn.wrist2 = 'a gator skin bracer';
          state.worn.waist = 'a studded leather belt with a crossed swords buckle';
          state.worn.sheath = '';
          break;  
        case 'combo_standard':
          state.worn.finger1 = state.worn.finger2 = 'a jade signet ring';
          state.worn.neck1 = state.worn.neck2 = 'a shimmering chain of gold';
          state.worn.cloak = 'a black, silver-embroidered cape';
          state.worn.wrist1 = state.worn.wrist2 = 'a sungwood bracelet';
          state.worn.waist = 'a bronze belt of odd design';
          state.worn.sheath = '';
          break;
        case 'combo_mixed':
          state.worn.finger1 = state.worn.finger2 = 'a jade signet ring';
          state.worn.neck1 = state.worn.neck2 = 'a shimmering chain of gold';
          state.worn.cloak = 'a black, silver-embroidered cape';
          state.worn.wrist1 = state.worn.wrist2 = 'a silver Kandori wristcuff';
          state.worn.waist = 'a belt with a buckle of cuendillar';
          state.worn.sheath = '';
          break;
        case 'combo_rare':
          state.worn.finger1 = state.worn.finger2 = 'a jade signet ring';
          state.worn.neck1 = state.worn.neck2 = 'a silver medallion';
          state.worn.cloak = 'a black, silver-embroidered cape';
          state.worn.wrist1 = state.worn.wrist2 = 'a jeweled wristcuff';
          state.worn.waist = 'a belt bearing a dragon worked in silver';
          state.worn.sheath = 'a heron-engraved scabbard';
          break;  
        case 'dodge_basic':
          state.worn.finger1 = state.worn.finger2 = 'an emerald ring';
          state.worn.neck1 = state.worn.neck2 = 'a Kandori snowflake necklace';
          state.worn.cloak = 'a bright red robe'; 
          state.worn.wrist1 = state.worn.wrist2 = 'a silver Kandori wristcuff';
          state.worn.waist = 'a belt with a buckle of cuendillar';
          state.worn.sheath = '';
          break;
        case 'dodge_standard':
          state.worn.finger1 = state.worn.finger2 = 'a gold ring';
          state.worn.neck1 = state.worn.neck2 = 'a shimmering pendant of obsidian';
          state.worn.cloak = 'a bright red robe'; 
          state.worn.wrist1 = state.worn.wrist2 = 'a silver Kandori wristcuff';
          state.worn.waist = 'a belt with a buckle of cuendillar';
          state.worn.sheath = '';
          break;
        case 'dodge_rare':
          state.worn.finger1 = state.worn.finger2 = 'a gold ring';
          state.worn.neck1 = state.worn.neck2 = 'a silver medallion';
          state.worn.cloak = 'a surcoat with exquisite embroidery';
          state.worn.wrist1 = state.worn.wrist2 = 'a jeweled wristcuff';
          state.worn.waist = 'a belt bearing a dragon worked in silver';
          state.worn.sheath = 'a heron-engraved scabbard';
          break;   
      }
    },

    clearFilters: (state) => {
      state.weaponType = '';
      state.weaponHands = 0;
      state.sortWeapons = '';
      state.sortArmor = '';
    },

    reset: (state) => {
    },

    queryEquipmentFulfilled: (state, action: PayloadAction<Equipment[]>) => {
      state.equipment = action.payload.filter(x => {
        return x.weight && (x.damage || x.ob || x.db || x.pb || x.abs || x.mvs) && !x.setup.includes('horse');
      });
    },
    
    getTrainerResults: (state) => {
      calcTrainerResults(state);
    }
  }
});

export const queryEquipment = (): AppThunk<Promise<Equipment[]>> => async (dispatch) => {  
  const url = 'https://wotcodeapi.azurewebsites.net/api/equipment';
  const results = await fetch(url).then(resp => resp.json()) as Equipment[];
  dispatch(slice.actions.queryEquipmentFulfilled(results));
  return results;
};

export default slice.reducer;
export const {
  reset,
  setStrn,
  setIntl,
  setWilp,
  setDext,
  setCont,
  setRaceId,
  setProfessionId,
  setLevel,
  setRank,
  setWeapon,
  setDodge,
  setShield,
  setRide,
  setWeight,
  setSelectedToggles,
  getTrainerResults,
  setWornItem,
  setGroupWeapons,
  setWeaponType,
  setWeaponHands,
  setSortWeapons,
  setSortArmor,
  setBash,
  setKick,
  setStab,
  setHide,
  setSneak,
  setNotice,
  setCharge,
  setAttack,
  setTerrain,
  setOpponentLevel,
  setOpponentOb,
  setOpponentDb,
  setOpponentPb,
  setOpponentNotice,
  setOpponentWeaponWeight,
  setOpponentClubs,
  setOpponentSat,
  applyOpponentBuild,
  applyArmorSet,
  applyTrinketSet,
  removeAll,
  clearFilters
} = slice.actions;

//TO DO: add getTrainerResultsPending, getTrainerResultsRejected